/**
 * Fonts the storefront loads, with their CSS variables.
 *
 * Most entries load through next/font/google in the storefront layout;
 * Google Sans and Xmind are not in the bundled next/font data, so those two
 * are self-hosted from public/fonts with @font-face in globals.css. Every
 * entry covers Cyrillic: the starter ships 36 locales including Russian, and
 * a font without Cyrillic glyphs falls back to an arbitrary system face,
 * which is what "broken font" complaints come from.
 *
 * Fonts without Cyrillic (outfit, geist) stay out of the list, but
 * LEGACY_FONT_FALLBACK keeps stored themes that reference them rendering:
 * outfit maps to Google Sans (same geometric-display feel, now with
 * Cyrillic), geist to Inter.
 */
export const FONTS = {
  inter: { label: "Inter", cssVar: "var(--font-inter)" },
  manrope: { label: "Manrope", cssVar: "var(--font-manrope)" },
  "google-sans": {
    label: "Google Sans",
    cssVar: "var(--font-google-sans)",
  },
  xmind: { label: "Xmind", cssVar: "var(--font-xmind)" },
  arimo: { label: "Arimo", cssVar: "var(--font-arimo)" },
  exo2: { label: "Exo 2", cssVar: "var(--font-exo2)" },
  montserrat: { label: "Montserrat", cssVar: "var(--font-montserrat)" },
  "noto-sans": { label: "Noto Sans", cssVar: "var(--font-noto-sans)" },
  "noto-serif": { label: "Noto Serif", cssVar: "var(--font-noto-serif)" },
  "nunito-sans": {
    label: "Nunito Sans",
    cssVar: "var(--font-nunito-sans)",
  },
  "open-sans": { label: "Open Sans", cssVar: "var(--font-open-sans)" },
  "playfair-display": {
    label: "Playfair Display",
    cssVar: "var(--font-playfair-display)",
  },
  "pt-sans": { label: "PT Sans", cssVar: "var(--font-pt-sans)" },
  roboto: { label: "Roboto", cssVar: "var(--font-roboto)" },
  "roboto-flex": {
    label: "Roboto Flex",
    cssVar: "var(--font-roboto-flex)",
  },
  rubik: { label: "Rubik", cssVar: "var(--font-rubik)" },
} as const

export type FontKey = keyof typeof FONTS

/** Type guard for API validation: is this a selectable font key? */
export function isKnownFontKey(font: unknown): font is FontKey {
  return typeof font === "string" && font in FONTS
}

/** Look up a font entry, falling back to Inter for unknown keys. */
export function getFont(font: string | undefined | null) {
  return font && font in FONTS ? FONTS[font as FontKey] : FONTS.inter
}

/** Fonts without Cyrillic, kept only so stored themes keep rendering. */
const LEGACY_FONT_FALLBACK: Record<string, FontKey> = {
  outfit: "google-sans",
  geist: "inter",
}

/**
 * Map a stored font key to a selectable one. Returns null for unknown keys
 * so callers can fall back to the preset's own font.
 */
export function normalizeFontKey(font: string | undefined | null): FontKey | null {
  if (!font) return null
  if (font in FONTS) return font as FontKey
  return LEGACY_FONT_FALLBACK[font] ?? null
}

/** Default font key used when a theme (or preset) does not set one. */
export const DEFAULT_FONT_KEY: FontKey = "inter"

export type FontPair = {
  label: string
  heading: FontKey
  body: FontKey
}

/**
 * Built-in font pairs: a heading font and a body font. Picked using the
 * Google Fonts pairing lessons (contrast between a display face and a
 * workhorse, similar x-height):
 * https://fonts.google.com/knowledge/choosing_type/pairing_typefaces
 */
export const FONT_PAIRS: Record<string, FontPair> = {
  classic: {
    label: "Playfair Display + Noto Sans",
    heading: "playfair-display",
    body: "noto-sans",
  },
  editorial: {
    label: "Noto Serif + Arimo",
    heading: "noto-serif",
    body: "arimo",
  },
  geometric: {
    label: "Montserrat + PT Sans",
    heading: "montserrat",
    body: "pt-sans",
  },
  modern: {
    label: "Manrope + Roboto",
    heading: "manrope",
    body: "roboto",
  },
  techy: {
    label: "Exo 2 + Open Sans",
    heading: "exo2",
    body: "open-sans",
  },
  friendly: {
    label: "Rubik + Nunito Sans",
    heading: "rubik",
    body: "nunito-sans",
  },
}

/** Pair applied when a theme does not set one. */
export const DEFAULT_PAIR_KEY: keyof typeof FONT_PAIRS = "classic"

/** Preset options for admin pickers, in registry order. */
export const PAIR_OPTIONS = Object.entries(FONT_PAIRS).map(
  ([value, pair]) => ({ value, label: pair.label })
)

/**
 * Resolve a stored font selection to a concrete pair. Precedence: an
 * explicit pair key, then stored heading+body keys, then the default pair.
 */
export function resolveFontPair(selection?: {
  pair?: string
  heading?: string
  body?: string
}): FontPair {
  if (selection?.pair && selection.pair in FONT_PAIRS) {
    return FONT_PAIRS[selection.pair]
  }
  const heading = normalizeFontKey(selection?.heading)
  if (heading) {
    const body = normalizeFontKey(selection?.body) ?? heading
    return {
      label: `${FONTS[heading].label} + ${FONTS[body].label}`,
      heading,
      body,
    }
  }
  return FONT_PAIRS[DEFAULT_PAIR_KEY]
}
